import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ShoppingBag, Wallet, Bell, Settings } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, brl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar, NotificationBell, EnableAlertsButton } from "@/components/Shell";

const STATUS_LABEL = {
  pending: "Novo", accepted: "Aceito", preparing: "Em preparo",
  ready: "Pronto p/ entrega", picked_up: "A caminho", delivered: "Entregue",
};

export default function MerchantDashboard() {
  const { store } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    const load = () => api.get("/merchant/dashboard").then(({ data }) => setD(data)).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  if (!d) return <div className="p-6 text-sm text-gray-500">Carregando painel…</div>;

  return (
    <div data-testid="merchant-dashboard">
      <TopBar subtitle={store?.name || "Painel do lojista"} right={<>
        <EnableAlertsButton />
        <Link to="/loja/config" data-testid="merchant-settings-link" className="p-2 rounded-full hover:bg-gf-gray gf-press inline-flex">
          <Settings size={19} className="text-gray-600" />
        </Link>
        <NotificationBell />
      </>} />
      <div className="px-4 pt-5">
        {d.new_orders > 0 && (
          <div
            data-testid="new-order-alert"
            className="rounded-2xl bg-gf-orange/10 border-2 border-gf-orange p-4 flex items-center gap-3 animate-pulse-ring"
          >
            <Bell size={20} className="text-gf-orange" />
            <div>
              <p className="font-display font-extrabold text-gf-black">
                {d.new_orders} novo{d.new_orders > 1 ? "s" : ""} pedido{d.new_orders > 1 ? "s" : ""}!
              </p>
              <p className="text-xs text-gray-600">Vá em Pedidos para aceitar.</p>
            </div>
          </div>
        )}

        <h1 className="font-display text-2xl font-extrabold mt-4">Hoje</h1>
        <div className="grid grid-cols-2 gap-3 mt-3 gf-stagger">
          <Kpi testId="kpi-orders-today" icon={ShoppingBag} label="Pedidos do dia" value={d.orders_today} />
          <Kpi testId="kpi-gross-today" icon={TrendingUp} label="Faturamento bruto" value={brl(d.gross_today)} />
          <Kpi testId="kpi-net-today" icon={Wallet} label="Líquido (após taxas)" value={brl(d.net_today)} accent />
          <Kpi testId="kpi-net-total" icon={Wallet} label="Líquido acumulado" value={brl(d.net_total)} />
        </div>

        <h2 className="font-display font-bold text-lg mt-7">Vendas dos últimos 7 dias</h2>
        <div className="mt-3 rounded-2xl border border-gray-200 p-3 bg-white" data-testid="sales-chart">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={d.series}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEE" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => brl(v)} />
              <Bar dataKey="vendas" fill="#FF7A1A" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <h2 className="font-display font-bold text-lg mt-7">Pedidos em andamento</h2>
        <div className="grid gap-2 mt-3" data-testid="in-progress-orders">
          {d.in_progress.map((o) => (
            <div key={o.id} className="rounded-2xl border border-gray-200 p-3 bg-white flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">{o.code} · {o.customer_name}</p>
                <p className="text-[11px] text-gray-500">{o.items.reduce((a, i) => a + i.qty, 0)} itens · {brl(o.total)}</p>
              </div>
              <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-gf-black text-white">
                {STATUS_LABEL[o.status]}
              </span>
            </div>
          ))}
          {d.in_progress.length === 0 && <p className="text-sm text-gray-500">Nenhum pedido em andamento.</p>}
        </div>
      </div>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, accent, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-gray-200 p-4 bg-white"
    style={accent ? { borderColor: "#FF7A1A" } : undefined}
  >
    <Icon size={17} className={accent ? "text-gf-orange" : "text-gray-400"} />
    <p className="text-[11px] text-gray-500 mt-2">{label}</p>
    <p className="font-display text-xl font-extrabold text-gf-black leading-tight">{value}</p>
  </div>
);
